'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { Container, Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { Button } from '@quark/button'
import { Reveal, MultiReveal, Animations } from '@quark/animation'
import BusinessModals from '@components/BusinessModal/businessModals'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import isMobile from 'ismobilejs'
import styles from './categoryProductCard.module.scss'

const themeMap = {
  dark: {
    textColor: 'base/inverted primary' as const,
    buttonTextColor: 'base/primary' as const,
    buttonType: 'staticOnDarkSolid' as const,
  },
  light: {
    textColor: 'base/primary' as const,
    buttonTextColor: 'base/inverted primary' as const,
    buttonType: 'staticOnLightSolid' as const,
  },
} as const

const CategoryProductCard = data => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isMobileDevice = isMobile()?.phone
  const theme = themeMap[data?.theme || 'light']
  const isDarkTheme = data?.theme === 'dark'

  const handleButtonClick = () => {
    if (data?.actionType === 'externalLink') {
      window.open(data?.redirectionLink, '_blank')
    } else {
      setIsModalOpen(true)
    }
  }

  return (
    <div className={isDarkTheme ? styles.darkBackground : styles.lightBackground}>
      <Container
        type="fluid"
        backgroundColor={isDarkTheme ? 'none' : 'base/elevated'}
        paddingTop={{ lg: 'spacing-120', md: 'spacing-80', sm: 'spacing-40' }}
        paddingBottom={{ lg: 'spacing-120', md: 'spacing-80', sm: 'spacing-40' }}
        paddingLeft="spacing-16"
        paddingRight="spacing-16">
        <Container type="root">
          <div className={styles.benefitsCardSection}>
            <div className={styles.leftContent}>
              <Flex
                flexDirection="column"
                gap={{
                  lg: 'spacing-32',
                  md: 'spacing-24',
                  sm: 'spacing-24',
                }}>
                <PropertyControlledComponent controllerProperty={!isMobileDevice} fallback={null}>
                  <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
                    {data?.tagline && (
                      <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
                        <Container paddingBottom="spacing-16">
                          <Text
                            as="h2"
                            fontStyle={{
                              lg: 'um_text-base/normal',
                              md: 'um_text-base/normal',
                              sm: 'um_text-base/normal',
                            }}
                            color={theme.textColor}>
                            {data?.tagline}
                          </Text>
                        </Container>
                      </Reveal>
                    )}
                    <Text
                      as="h2"
                      fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-3xl/bold', sm: 'um_text-2xl/bold' }}
                      color={theme.textColor}>
                      <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
                    </Text>
                  </Reveal>
                </PropertyControlledComponent>
                <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
                  <Flex
                    flexDirection="column"
                    gap={{
                      lg: 'spacing-20',
                      md: 'spacing-16',
                      sm: 'spacing-16',
                    }}>
                    <MultiReveal
                      as={Flex}
                      flexDirection="column"
                      gap={{
                        lg: 'spacing-20',
                        md: 'spacing-16',
                        sm: 'spacing-16',
                      }}
                      animationDelays={(i: number) => 300 + i * 100}
                      animationProps={{ animation: Animations.FadeIn50px }}>
                      {data?.benefitList?.map((benefit, index) => (
                        <div className={styles.benefitListItem} key={index}>
                          <Image
                            src={benefit?.iconUrl?.url}
                            alt="check-mark"
                            width={isMobileDevice ? 16 : 24}
                            height={isMobileDevice ? 16 : 24}
                          />
                          <Text
                            as="div"
                            fontStyle={{
                              lg: 'um_text-lg/normal',
                              md: 'um_text-base/normal',
                              sm: 'um_text-sm/normal',
                            }}
                            color={theme.textColor}>
                            {benefit?.title}
                          </Text>
                        </div>
                      ))}
                    </MultiReveal>
                  </Flex>
                </Reveal>
                <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
                  <Button type={theme.buttonType} size="lg" onClick={handleButtonClick}>
                    <Text fontStyle="um_text-lg/medium" color={theme.buttonTextColor}>
                      {data?.actionText}
                    </Text>
                  </Button>
                </Reveal>
              </Flex>
            </div>
            <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
              <div className={styles.rightContent}>
                <PropertyControlledComponent controllerProperty={isMobileDevice} fallback={null}>
                  <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
                    {data?.tagline && (
                      <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
                        <Container paddingBottom="spacing-8">
                          <Text as="h2" fontStyle="um_text-base/normal" color={theme.textColor} textAlign="center">
                            {data?.tagline}
                          </Text>
                        </Container>
                      </Reveal>
                    )}
                    <Container paddingBottom="spacing-64">
                      <Text as="h2" fontStyle="um_text-2xl/bold" color={theme.textColor} textAlign="center">
                        <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
                      </Text>
                    </Container>
                  </Reveal>
                </PropertyControlledComponent>
                <Container position="relative">
                  <Flex display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <PropertyControlledComponent controllerProperty={data?.heroImage?.url} fallback={null}>
                      <img src={data?.heroImage?.url} alt="Claim resolved on phone" className={styles.heroImage} />
                    </PropertyControlledComponent>
                  </Flex>
                  <div className={styles.floatingImages}>
                    <PropertyControlledComponent controllerProperty={data?.floatingImages} fallback={null}>
                      {data?.floatingImages?.map(image => (
                        <img src={image?.url} className={styles.floatingImageItem} />
                      ))}
                    </PropertyControlledComponent>
                  </div>
                </Container>
              </div>
            </Reveal>
          </div>
        </Container>
      </Container>
      <BusinessModals isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} content={data?.formSnippet} />
    </div>
  )
}

export default CategoryProductCard
